


import { TagOportunidade } from '../../../shared/services/models/vendas/oportunidade.model';


export type StatusClienteChave = number;

export interface StatusClienteInfo {
  chave: StatusClienteChave;
  label: string;
  cor: string;
}


const COR_POR_DESCRICAO: Record<string, string> = {
  lead: '#757575',
  prospect: '#1E5DB8',
  cliente: '#1E8E3E',
  // #C4C7CF (cinza claro) era ilegível sobre fundo escuro: com opacity reduzida no ícone do
  // menu ⋮, praticamente sumia. #9E9E9E mantém "apagado" nos dois temas sem desaparecer.
  'ex-cliente': '#9E9E9E',
  inativo: '#9E9E9E',
};

const COR_STATUS_PADRAO = '#9AA0A6';

export function corDoStatusCrm(descricao: string): string {
  const chave = (descricao || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  return COR_POR_DESCRICAO[chave] ?? COR_STATUS_PADRAO;
}


export const STATUS_CLIENTE_INDEFINIDO: StatusClienteInfo = {
  chave: 0,
  label: 'Sem status',
  cor: COR_STATUS_PADRAO,
};

export interface Person {
  idUsuario: number;
  name: string;
  color: string;
  initials: string;
}

/** Cores dos avatares (usuários e vendedores). */
export const AVATAR_COLORS = ['#B73232', '#1E5DB8', '#1E8E3E', '#7A4FBF', '#C26A00', '#0F7A8B'];


export type PrioridadeChave = 'alta' | 'media' | 'baixa';

export interface PrioridadeInfo {
  chave: PrioridadeChave;
  label: string;
  cor: string;
}

export const PRIORIDADES: PrioridadeInfo[] = [
  { chave: 'alta', label: 'Alta', cor: '#F44336' },
  { chave: 'media', label: 'Média', cor: '#FFC107' },
  { chave: 'baixa', label: 'Baixa', cor: '#4CAF50' },
];

export const PRIORIDADE_POR_CHAVE: Record<PrioridadeChave, PrioridadeInfo> = PRIORIDADES.reduce(
  (acc, p) => ({ ...acc, [p.chave]: p }),
  {} as Record<PrioridadeChave, PrioridadeInfo>
);



export const PRIORIDADE_POR_ID: Record<number, PrioridadeChave> = {
  1: 'baixa',
  2: 'media',
  3: 'alta',
};

export const ID_POR_PRIORIDADE: Record<PrioridadeChave, number> = {
  baixa: 1,
  media: 2,
  alta: 3,
};


/** Uma alteração do diff de auditoria gravado em dados ({"alteracoes": [...]}) da ocorrência. */
export interface AuditChange {
  campo: string;
  label: string;
  acao: 'alterado' | 'adicionado' | 'removido';
  item?: string | null;
  
  de?: string | null;
  para?: string | null;
}

export interface OportunidadeCard {
  id: string;
  idOportunidade: number;
  
  stage: string;
  
  idEtapaKanban: number;

  titulo: string;

  idCliente: number;
  cliente: string;
  
  idContato: number | null;
  contato: string | null;
  
  contatoEmail: string | null;

  /** Vendedor do cadastro de Pessoa; nome, iniciais e cor vêm do lookup de vendedores. */
  idVendedorResponsavel: number;
  resp: string;
  respName: string;
  respColor: string;
  /** "Criado por": usuário e data-hora (da API, sem fuso) da inclusão. */
  idUsuarioInclusao: number | null;
  datetimeInclusao: string;
  /** Usuário responsável pela próxima ação pendente. */
  idUsuarioResponsavelAcao: number | null;

  origem: string;
  idOrigemLead: number;
  idCanalContato: number | null;
  valorEstimado: number;
  /** Valor da oportunidade: o do documento de Vendas que o define ou, sem ele, o estimado. */
  valor: number;
  /** Documento que define o valor; null quando o valor é o estimado. */
  idVendaValor: number | null;
  /** Valores de VendaTipoDocumentoEnum do documento que define o valor. */
  vendaTipoDocumentoValor: number | null;
  numeroDocumentoValor: number | null;
  prioridade: PrioridadeChave;

  proximaAcao: string | null;
  
  dataProximaAcao: string | null;

  
  tags: TagOportunidade[];
  arquivado: boolean;
  /** Favorita do usuário logado (a marcação é pessoal). */
  isFavorito: boolean;
  
  criadoEm: string;

  
  camposValores: Record<number, string>;
  
  resultado?: string;
  motivo?: string;
  idCampoDefinicaoOpcaoResultado?: number | null;
  idCampoDefinicaoOpcaoMotivo?: number | null;
  observacoesFinais?: string;

  
  updatedAt?: number;
  renderKey?: string;
}


export interface OportunidadeStage {
  key: string;
  idEtapaKanban: number;
  name: string;
  
  descricao: string;
  icon: string;
  color: string;
  tint: string;
  ordem: number;
  
  isEspecial: boolean;
  
  isFinal: boolean;
  
  idEtapaKanbanRetornoReabertura: number | null;
  
  podeVisualizar?: boolean;
}

export type VisaoBoard = 'ativas' | 'encerradas' | 'favoritas' | 'arquivadas' | 'pendentes';

export type PeriodoBoard = 'todas' | 'hoje' | 'semana' | 'mes' | '30d' | 'intervalo';


export const PERIODO_PADRAO: PeriodoBoard = 'todas';

export interface OpcaoRotulada<T> {
  value: T;
  label: string;
}

export const VISAO_OPCOES: OpcaoRotulada<VisaoBoard>[] = [
  { value: 'ativas', label: 'Ativas' },
  { value: 'encerradas', label: 'Encerradas' },
  { value: 'favoritas', label: 'Favoritas' },
  { value: 'arquivadas', label: 'Arquivadas' },
  { value: 'pendentes', label: 'Com ações pendentes' },
];


export const CONTADOR_SUBSTANTIVO: Record<VisaoBoard, { singular: string; plural: string }> = {
  ativas: { singular: 'oportunidade', plural: 'oportunidades' },
  encerradas: { singular: 'encerrada', plural: 'encerradas' },
  favoritas: { singular: 'favorita', plural: 'favoritas' },
  arquivadas: { singular: 'arquivada', plural: 'arquivadas' },
  pendentes: { singular: 'com ação pendente', plural: 'com ações pendentes' },
};

export const PERIODO_OPCOES: OpcaoRotulada<PeriodoBoard>[] = [
  { value: 'todas', label: 'todas' },
  { value: 'hoje', label: 'hoje' },
  { value: 'semana', label: 'desta semana' },
  { value: 'mes', label: 'do mês' },
  { value: '30d', label: 'últimos 30 dias' },
  { value: 'intervalo', label: 'do intervalo' },
];


export type CampoDataFiltro = 'criacao' | 'ultimaAlteracao' | 'proximaAcao' | 'encerramento';


export const CAMPO_DATA_PADRAO: CampoDataFiltro = 'criacao';

export const CAMPO_DATA_OPCOES: OpcaoRotulada<CampoDataFiltro>[] = [
  { value: 'criacao', label: 'Data de criação' },
  { value: 'ultimaAlteracao', label: 'Última alteração' },
  { value: 'proximaAcao', label: 'Próxima ação' },
  { value: 'encerramento', label: 'Encerramento' },
];


export const ID_POR_CAMPO_DATA: Record<CampoDataFiltro, number> = {
  criacao: 0,
  ultimaAlteracao: 1,
  proximaAcao: 2,
  encerramento: 3,
};


export const TIPO_CAMPO = {
  texto: 1,
  numero: 2,
  moeda: 3,
  data: 4,
  selecao: 5,
  selecaoMultipla: 6,
  vinculo: 7,
  anexo: 8,
  booleano: 9,
  textoLongo: 10,
  percentual: 11,
} as const;

export const FAIXA_VALOR_OPCOES: OpcaoRotulada<string>[] = [
  { value: 'lt10', label: 'Até R$ 10 mil' },
  { value: '10a50', label: 'R$ 10 mil a R$ 50 mil' },
  { value: 'gt50', label: 'Acima de R$ 50 mil' },
];
